<script setup>
import camelcaseKeys from 'camelcase-keys';
import { debounce } from '@chatwoot/utils';
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import {
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfMonth,
  endOfToday,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isWithinInterval,
  parseISO,
  setHours,
  subMinutes,
  startOfMonth,
  startOfToday,
  startOfWeek,
  subMonths,
  subWeeks,
} from 'date-fns';
import { es as localeEs } from 'date-fns/locale';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ContactAPI from 'dashboard/api/contacts';
import CreateNewContactDialog from 'dashboard/components-next/Contacts/ContactsForm/CreateNewContactDialog.vue';

const WEEK_STARTS_ON = 1;
const DEFAULT_DURATION = 30;
const DEFAULT_SOURCE = 'manual';

const store = useStore();
const { t, locale } = useI18n();

const agendaEvents = useMapGetter('agenda/getAgendaEvents');
const uiFlags = useMapGetter('agenda/getUIFlags');
const inboxes = useMapGetter('inboxes/getInboxes');

const viewMode = ref('list');
const calendarCursor = ref(startOfToday());
const dialogRef = ref(null);
const quickCreateContactDialogRef = ref(null);
const editingEventId = ref(null);
const selectedContactId = ref('');
const contactOptions = ref([]);
const isSearchingContacts = ref(false);
const contactsRequestController = ref(null);
const shownReminderKeys = ref([]);
let reminderInterval = null;

const buildDefaultScheduledAt = () =>
  format(new Date(), "yyyy-MM-dd'T'HH:mm");

const buildDefaultForm = () => ({
  scheduledAt: buildDefaultScheduledAt(),
  contactId: '',
  conversationId: '',
  contactName: '',
  reason: '',
  durationMinutes: DEFAULT_DURATION,
  reminderEnabled: false,
  reminderValue: 1,
  reminderUnit: 'hours',
  reminderActionType: 'message',
  reminderTemplateKey: '',
  reminderTemplatePayload: {},
  reminderMessage: '',
  status: 'pending',
  source: DEFAULT_SOURCE,
});

const form = reactive(buildDefaultForm());

const statusOptions = computed(() => [
  { value: 'pending', label: t('AGENDA.STATUS.PENDING') },
  { value: 'completed', label: t('AGENDA.STATUS.COMPLETED') },
  { value: 'canceled', label: t('AGENDA.STATUS.CANCELED') },
]);

const sourcePlaceholder = computed(() =>
  t('AGENDA.FIELDS.SOURCE')
);

const isLoading = computed(() => uiFlags.value.isFetching);
const isSubmitting = computed(
  () => uiFlags.value.isCreating || uiFlags.value.isUpdating
);
const contactPickerMessage = computed(() =>
  isSearchingContacts.value
    ? t('SEARCH.LOADING_DATA')
    : t('AGENDA.FIELDS.EXISTING_CONTACT_HELP')
);
const canSubmitAgenda = computed(() => {
  const isValidDate = !!form.scheduledAt && !Number.isNaN(new Date(form.scheduledAt).getTime());
  const isValidReminder =
    !form.reminderEnabled ||
    (Number(form.reminderValue) > 0 &&
      !!form.contactId &&
      (form.reminderActionType !== 'template' || !!form.reminderTemplateKey));
  return (
    isValidDate &&
    isValidReminder &&
    !!form.contactName.trim() &&
    !!form.reason.trim() &&
    Number(form.durationMinutes) > 0
  );
});

const parseEventDate = value => parseISO(value);

const activeDateLocale = computed(() =>
  locale.value?.toLowerCase().startsWith('es') ? localeEs : undefined
);

const formatLocalizedDate = (value, pattern) =>
  format(value, pattern, { locale: activeDateLocale.value });

const formatDateTime = value =>
  formatLocalizedDate(parseEventDate(value), "d 'de' MMM yyyy HH:mm");

const formatDuration = minutes =>
  t('AGENDA.DURATION_MINUTES', { count: minutes });

const toDateTimeLocalValue = value =>
  value ? format(parseEventDate(value), "yyyy-MM-dd'T'HH:mm") : buildDefaultScheduledAt();

const normalizedEvents = computed(() =>
  agendaEvents.value.map(event => ({
    ...event,
    date: parseEventDate(event.scheduledAt),
  }))
);

const todayStart = computed(() => startOfToday());
const todayEnd = computed(() => endOfToday());

const currentWeekStart = computed(() =>
  startOfWeek(calendarCursor.value, { weekStartsOn: WEEK_STARTS_ON })
);
const currentWeekEnd = computed(() =>
  endOfWeek(calendarCursor.value, { weekStartsOn: WEEK_STARTS_ON })
);
const currentMonthStart = computed(() => startOfMonth(calendarCursor.value));
const currentMonthEnd = computed(() => endOfMonth(calendarCursor.value));

const summaryCards = computed(() => {
  const todayCount = normalizedEvents.value.filter(event =>
    isWithinInterval(event.date, {
      start: todayStart.value,
      end: todayEnd.value,
    })
  ).length;

  const thisWeekCount = normalizedEvents.value.filter(event =>
    isWithinInterval(event.date, {
      start: startOfWeek(todayStart.value, { weekStartsOn: WEEK_STARTS_ON }),
      end: endOfWeek(todayStart.value, { weekStartsOn: WEEK_STARTS_ON }),
    })
  ).length;

  const pendingCount = normalizedEvents.value.filter(
    event => event.status === 'pending'
  ).length;
  const completedCount = normalizedEvents.value.filter(
    event => event.status === 'completed'
  ).length;

  return [
    { key: 'today', label: t('AGENDA.SUMMARY.TODAY'), value: todayCount },
    {
      key: 'week',
      label: t('AGENDA.SUMMARY.THIS_WEEK'),
      value: thisWeekCount,
    },
    {
      key: 'pending',
      label: t('AGENDA.SUMMARY.PENDING'),
      value: pendingCount,
    },
    {
      key: 'completed',
      label: t('AGENDA.SUMMARY.COMPLETED'),
      value: completedCount,
    },
  ];
});

const weekDays = computed(() =>
  eachDayOfInterval({
    start: currentWeekStart.value,
    end: currentWeekEnd.value,
  })
);

const monthDays = computed(() =>
  eachDayOfInterval({
    start: startOfWeek(currentMonthStart.value, {
      weekStartsOn: WEEK_STARTS_ON,
    }),
    end: endOfWeek(currentMonthEnd.value, {
      weekStartsOn: WEEK_STARTS_ON,
    }),
  })
);

const weekdayHeaders = computed(() =>
  weekDays.value.map(day => formatLocalizedDate(day, 'EEE'))
);

const hourSlots = computed(() => Array.from({ length: 24 }, (_, hour) => hour));

const displayedPeriodLabel = computed(() => {
  if (viewMode.value === 'month') {
    return formatLocalizedDate(calendarCursor.value, 'MMMM yyyy');
  }

  if (viewMode.value === 'week') {
    return `${formatLocalizedDate(currentWeekStart.value, "d 'de' MMM")} - ${formatLocalizedDate(
      currentWeekEnd.value,
      "d 'de' MMM yyyy"
    )}`;
  }

  return t('AGENDA.TITLE');
});

const eventsForDay = day =>
  normalizedEvents.value.filter(event => isSameDay(event.date, day));

const eventsForDayAndHour = (day, hour) =>
  eventsForDay(day).filter(event => event.date.getHours() === hour);

const formatHourLabel = hour =>
  formatLocalizedDate(setHours(new Date(), hour), 'HH:mm');

const listEvents = computed(() => normalizedEvents.value);

const buildTemplateOptionValue = template =>
  `${template.__inboxId || 0}::${template.name}::${template.language || 'en'}`;

const availableWhatsAppTemplates = computed(() =>
  inboxes.value.flatMap(inbox =>
    store.getters['inboxes/getFilteredWhatsAppTemplates'](inbox.id).map(
      template => ({
        ...template,
        __inboxId: inbox.id,
        __inboxName: inbox.name,
      })
    )
  )
);

const reminderTemplateOptions = computed(() =>
  availableWhatsAppTemplates.value.map(template => ({
    value: buildTemplateOptionValue(template),
    label: `${template.name} (${template.language || 'en'}) - ${template.__inboxName}`,
  }))
);

const reminderCandidates = computed(() => {
  const now = new Date();

  return normalizedEvents.value.filter(event => {
    if (event.status !== 'pending' || !event.reminderEnabled) {
      return false;
    }

    const minutesBefore = Number(event.reminderMinutesBefore || 0);
    if (minutesBefore <= 0) {
      return false;
    }

    const reminderAt = subMinutes(event.date, minutesBefore);
    return now >= reminderAt && now <= event.date;
  });
});

const selectedContact = computed(() =>
  contactOptions.value.find(
    contact => String(contact.value) === String(selectedContactId.value)
  )
);

const resetForm = () => {
  Object.assign(form, buildDefaultForm());
  editingEventId.value = null;
  selectedContactId.value = '';
};

const loadContactOptions = async searchQuery => {
  contactsRequestController.value?.abort?.();
  const controller = new AbortController();
  contactsRequestController.value = controller;
  isSearchingContacts.value = true;

  try {
    const {
      data: { payload = [] },
    } = await ContactAPI.search(searchQuery, 1, 'name', '', {
      signal: controller.signal,
    });

    contactOptions.value = camelcaseKeys(payload, { deep: true }).map(contact => {
      const metadata = [contact.email, contact.phoneNumber]
        .filter(Boolean)
        .join(' · ');

      return {
        value: String(contact.id),
        label: metadata ? `${contact.name} (${metadata})` : contact.name,
        name: contact.name,
        email: contact.email,
        phoneNumber: contact.phoneNumber,
      };
    });
  } catch (error) {
    if (error.name !== 'CanceledError' && error.name !== 'AbortError') {
      contactOptions.value = [];
    }
  } finally {
    if (contactsRequestController.value === controller) {
      isSearchingContacts.value = false;
    }
  }
};

const debouncedLoadContacts = debounce(searchQuery => {
  loadContactOptions(searchQuery);
}, 250);

const primeContactSearch = async () => {
  if (contactOptions.value.length) {
    return;
  }
  await loadContactOptions('');
};

const openCreateDialog = async () => {
  resetForm();
  dialogRef.value?.open?.();
  primeContactSearch();
};

const openEditDialog = async agendaEvent => {
  editingEventId.value = agendaEvent.id;
  Object.assign(form, {
    scheduledAt: toDateTimeLocalValue(agendaEvent.scheduledAt),
    contactId: agendaEvent.contactId ? String(agendaEvent.contactId) : '',
    conversationId: agendaEvent.conversationId ? String(agendaEvent.conversationId) : '',
    contactName: agendaEvent.contactName,
    reason: agendaEvent.reason,
    durationMinutes: agendaEvent.durationMinutes,
    reminderEnabled: !!agendaEvent.reminderEnabled,
    reminderValue: Number(agendaEvent.reminderValue || 1),
    reminderUnit: agendaEvent.reminderUnit || 'hours',
    reminderActionType: agendaEvent.reminderActionType || 'message',
    reminderTemplateKey: agendaEvent.reminderTemplatePayload?.name
      ? buildTemplateOptionValue({
          ...agendaEvent.reminderTemplatePayload,
          __inboxId:
            agendaEvent.reminderTemplatePayload?.inboxId ||
            agendaEvent.reminderTemplatePayload?.inbox_id ||
            0,
        })
      : '',
    reminderTemplatePayload: agendaEvent.reminderTemplatePayload || {},
    reminderMessage: agendaEvent.reminderMessage || '',
    status: agendaEvent.status,
    source: agendaEvent.source || DEFAULT_SOURCE,
  });
  selectedContactId.value = agendaEvent.contactId ? String(agendaEvent.contactId) : '';
  dialogRef.value?.open?.();
  primeContactSearch();
};

const closeDialog = () => {
  resetForm();
  dialogRef.value?.close?.();
};

const openQuickCreateContactDialog = () => {
  quickCreateContactDialogRef.value?.dialogRef.open();
};

const createQuickContact = async contact => {
  try {
    const createdContact = await store.dispatch('contacts/create', contact);
    quickCreateContactDialogRef.value?.onSuccess();

    const normalizedContact = {
      value: String(createdContact.id),
      label: createdContact.email
        ? `${createdContact.name} (${createdContact.email})`
        : createdContact.phone_number
          ? `${createdContact.name} (${createdContact.phone_number})`
          : createdContact.name,
      name: createdContact.name,
      email: createdContact.email,
      phoneNumber: createdContact.phone_number,
    };

    contactOptions.value = [
      normalizedContact,
      ...contactOptions.value.filter(
        existing => existing.value !== normalizedContact.value
      ),
    ];
    selectedContactId.value = normalizedContact.value;
    form.contactId = normalizedContact.value;
    form.contactName = normalizedContact.name;
    if (!form.source.trim() || form.source.trim() === DEFAULT_SOURCE) {
      form.source = 'contact';
    }
    useAlert(t('AGENDA.ALERTS.CONTACT_CREATED'));
  } catch (error) {
    useAlert(t('AGENDA.ALERTS.CONTACT_CREATE_ERROR'));
  }
};

const submitAgendaEvent = async () => {
  if (!canSubmitAgenda.value) {
    return;
  }

  try {
    const scheduledAt = new Date(form.scheduledAt);
    if (Number.isNaN(scheduledAt.getTime())) {
      return;
    }

    const payload = {
      scheduled_at: scheduledAt.toISOString(),
      contact_id: form.contactId || null,
      conversation_id: form.conversationId || null,
      contact_name: form.contactName.trim(),
      reason: form.reason.trim(),
      duration_minutes: Number(form.durationMinutes),
      reminder_enabled: !!form.reminderEnabled,
      reminder_value: form.reminderEnabled ? Number(form.reminderValue || 1) : null,
      reminder_unit: form.reminderEnabled ? form.reminderUnit : null,
      reminder_action_type: form.reminderEnabled ? form.reminderActionType : null,
      reminder_minutes_before: form.reminderEnabled
        ? form.reminderUnit === 'days'
          ? Number(form.reminderValue || 1) * 24 * 60
          : Number(form.reminderValue || 1) * 60
        : null,
      reminder_message: form.reminderEnabled
        ? form.reminderMessage.trim()
        : '',
      reminder_template_payload:
        form.reminderEnabled && form.reminderActionType === 'template'
          ? reminderTemplatePayloadForSelection(form.reminderTemplateKey)
          : {},
      status: form.status,
      source: form.source.trim() || DEFAULT_SOURCE,
    };

    if (editingEventId.value) {
      await store.dispatch('agenda/update', {
        id: editingEventId.value,
        ...payload,
      });
      useAlert(t('AGENDA.ALERTS.UPDATED'));
    } else {
      await store.dispatch('agenda/create', payload);
      useAlert(t('AGENDA.ALERTS.CREATED'));
    }
    closeDialog();
  } catch (error) {
    // Error toast is handled by the API helper/store
  }
};

const deleteAgendaEvent = async agendaEvent => {
  if (!window.confirm(t('AGENDA.DELETE_CONFIRM'))) {
    return;
  }

  try {
    await store.dispatch('agenda/delete', agendaEvent.id);
    useAlert(t('AGENDA.ALERTS.DELETED'));
  } catch (error) {
    // Error toast is handled by the API helper/store
  }
};

const markCompleted = async agendaEvent => {
  if (agendaEvent.status === 'completed') {
    return;
  }

  try {
    await store.dispatch('agenda/complete', agendaEvent.id);
    useAlert(t('AGENDA.ALERTS.COMPLETED'));
  } catch (error) {
    // Error toast is handled by the API helper/store
  }
};

const goToToday = () => {
  calendarCursor.value = startOfToday();
};

const goToPrevious = () => {
  if (viewMode.value === 'month') {
    calendarCursor.value = subMonths(calendarCursor.value, 1);
    return;
  }

  calendarCursor.value = subWeeks(calendarCursor.value, 1);
};

const goToNext = () => {
  if (viewMode.value === 'month') {
    calendarCursor.value = addMonths(calendarCursor.value, 1);
    return;
  }

  calendarCursor.value = addWeeks(calendarCursor.value, 1);
};

const statusBadgeClass = status => {
  const classes = {
    pending:
      'border-n-amber-6/40 bg-n-amber-3 text-n-amber-11 dark:border-n-amber-9/40 dark:bg-n-amber-3/10 dark:text-n-amber-11',
    completed:
      'border-n-teal-6/40 bg-n-teal-3 text-n-teal-11 dark:border-n-teal-9/40 dark:bg-n-teal-3/10 dark:text-n-teal-11',
    canceled:
      'border-n-slate-6/40 bg-n-alpha-2 text-n-slate-11 dark:border-n-slate-7/40 dark:bg-n-alpha-2 dark:text-n-slate-11',
  };

  return classes[status] || classes.pending;
};

const statusLabel = status =>
  t(`AGENDA.STATUS.${status.toUpperCase()}`);

const reminderSummary = agendaEvent => {
  if (!agendaEvent.reminderEnabled) {
    return '';
  }

  return t('AGENDA.REMINDERS.SUMMARY', {
    value: agendaEvent.reminderValue,
    unit: t(`AGENDA.REMINDERS.UNITS.${(agendaEvent.reminderUnit || 'hours').toUpperCase()}`),
  });
};

const reminderTemplatePayloadForSelection = templateKey => {
  if (!templateKey) {
    return {};
  }

  const template = availableWhatsAppTemplates.value.find(
    item => buildTemplateOptionValue(item) === templateKey
  );

  if (!template) {
    return {};
  }

  return {
    name: template.name,
    category: template.category,
    language: template.language,
    namespace: template.namespace,
    inbox_id: template.__inboxId,
  };
};

const triggerAgendaReminders = () => {
  reminderCandidates.value.forEach(agendaEvent => {
    const reminderKey = `${agendaEvent.id}:${agendaEvent.scheduledAt}:${agendaEvent.reminderMinutesBefore}`;
    if (shownReminderKeys.value.includes(reminderKey)) {
      return;
    }

    shownReminderKeys.value = [...shownReminderKeys.value, reminderKey];
    const message =
      agendaEvent.reminderMessage ||
      t('AGENDA.REMINDERS.DEFAULT_MESSAGE', {
        contact: agendaEvent.contactName,
        reason: agendaEvent.reason,
        time: formatLocalizedDate(agendaEvent.date, 'HH:mm'),
      });

    useAlert(message);

    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
      // eslint-disable-next-line no-new
      new Notification(t('AGENDA.REMINDERS.BROWSER_TITLE'), {
        body: message,
      });
    }
  });
};

watch(selectedContact, contact => {
  if (!contact) {
    return;
  }

  form.contactId = contact.value;
  form.contactName = contact.name || contact.label;
});

watch(selectedContactId, contactId => {
  if (contactId) {
    return;
  }

  form.contactId = '';
  form.conversationId = '';
});

watch(
  () => form.reminderActionType,
  actionType => {
    if (actionType === 'template') {
      form.reminderMessage = '';
      return;
    }

    form.reminderTemplateKey = '';
    form.reminderTemplatePayload = {};
  }
);

watch(
  reminderCandidates,
  () => {
    triggerAgendaReminders();
  },
  { deep: true }
);

watch(viewMode, mode => {
  if (mode === 'week') {
    calendarCursor.value = startOfToday();
  }
});

onMounted(() => {
  store.dispatch('agenda/get');
  reminderInterval = window.setInterval(() => {
    triggerAgendaReminders();
  }, 30_000);
});

onBeforeUnmount(() => {
  if (reminderInterval) {
    window.clearInterval(reminderInterval);
  }
});
</script>

<template>
  <div class="flex h-full min-w-0 w-full flex-col overflow-hidden bg-n-surface-1">
    <div class="flex min-h-0 flex-1 w-full max-w-none flex-col gap-6 overflow-y-auto px-6 py-6">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div class="space-y-1">
          <h1 class="text-2xl font-semibold text-n-slate-12">
            {{ $t('AGENDA.TITLE') }}
          </h1>
          <p class="mb-0 text-sm text-n-slate-11">
            {{ $t('AGENDA.DESCRIPTION') }}
          </p>
        </div>
        <Button
          icon="i-lucide-plus"
          :label="$t('AGENDA.ADD')"
          sm
          @click="openCreateDialog"
        />
      </div>

      <div class="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <div
          v-for="card in summaryCards"
          :key="card.key"
          class="rounded-xl border border-n-weak bg-n-solid-1 p-4 shadow-sm"
        >
          <p class="mb-1 text-sm font-medium text-n-slate-11">
            {{ card.label }}
          </p>
          <p class="mb-0 text-3xl font-semibold text-n-slate-12">
            {{ card.value }}
          </p>
        </div>
      </div>

      <div class="rounded-2xl border border-n-weak bg-n-solid-1 shadow-sm">
        <div class="flex flex-col gap-4 border-b border-n-weak px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
          <div class="flex flex-wrap items-center gap-2">
            <Button
              variant="faded"
              color="slate"
              sm
              icon="i-lucide-chevron-left"
              :label="$t('AGENDA.NAVIGATION.PREVIOUS')"
              @click="goToPrevious"
            />
            <Button
              variant="faded"
              color="slate"
              sm
              :label="$t('AGENDA.NAVIGATION.TODAY')"
              @click="goToToday"
            />
            <Button
              variant="faded"
              color="slate"
              sm
              trailing-icon
              icon="i-lucide-chevron-right"
              :label="$t('AGENDA.NAVIGATION.NEXT')"
              @click="goToNext"
            />
            <span class="px-2 text-sm font-medium text-n-slate-12">
              {{ displayedPeriodLabel }}
            </span>
          </div>

          <div class="flex flex-wrap items-center gap-2">
            <Button
              :variant="viewMode === 'list' ? 'solid' : 'faded'"
              :color="viewMode === 'list' ? 'blue' : 'slate'"
              sm
              :label="$t('AGENDA.VIEWS.LIST')"
              @click="viewMode = 'list'"
            />
            <Button
              :variant="viewMode === 'week' ? 'solid' : 'faded'"
              :color="viewMode === 'week' ? 'blue' : 'slate'"
              sm
              :label="$t('AGENDA.VIEWS.WEEK')"
              @click="viewMode = 'week'"
            />
            <Button
              :variant="viewMode === 'month' ? 'solid' : 'faded'"
              :color="viewMode === 'month' ? 'blue' : 'slate'"
              sm
              :label="$t('AGENDA.VIEWS.MONTH')"
              @click="viewMode = 'month'"
            />
          </div>
        </div>

        <div v-if="isLoading" class="flex min-h-[20rem] items-center justify-center">
          <Spinner class="text-n-brand" />
        </div>

        <div
          v-else-if="!listEvents.length"
          class="flex min-h-[20rem] flex-col items-center justify-center gap-2 px-6 py-12 text-center"
        >
          <span
            class="inline-flex size-12 items-center justify-center rounded-full bg-n-brand/10 text-n-brand"
          >
            <Icon icon="i-lucide-calendar-range" class="size-6" />
          </span>
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{ $t('AGENDA.EMPTY_STATE.TITLE') }}
          </h2>
          <p class="mb-0 max-w-lg text-sm text-n-slate-11">
            {{ $t('AGENDA.EMPTY_STATE.DESCRIPTION') }}
          </p>
        </div>

        <div v-else-if="viewMode === 'list'" class="overflow-x-auto">
          <table class="min-w-full divide-y divide-n-weak">
            <thead class="bg-n-alpha-1">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.08em] text-n-slate-11">
                  {{ $t('AGENDA.TABLE.SCHEDULED_AT') }}
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.08em] text-n-slate-11">
                  {{ $t('AGENDA.TABLE.CONTACT') }}
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.08em] text-n-slate-11">
                  {{ $t('AGENDA.TABLE.REASON') }}
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.08em] text-n-slate-11">
                  {{ $t('AGENDA.TABLE.DURATION') }}
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.08em] text-n-slate-11">
                  {{ $t('AGENDA.TABLE.STATUS') }}
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.08em] text-n-slate-11">
                  {{ $t('AGENDA.TABLE.SOURCE') }}
                </th>
                <th class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-[0.08em] text-n-slate-11">
                  {{ $t('AGENDA.TABLE.ACTIONS') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr
                v-for="agendaEvent in listEvents"
                :key="agendaEvent.id"
                class="align-top"
              >
                <td class="px-4 py-3 text-sm text-n-slate-12">
                  <div class="flex flex-col gap-1">
                    <span>{{ formatDateTime(agendaEvent.scheduledAt) }}</span>
                    <span
                      v-if="agendaEvent.reminderEnabled"
                      class="text-xs text-n-brand"
                    >
                      {{ reminderSummary(agendaEvent) }}
                    </span>
                  </div>
                </td>
                <td class="px-4 py-3 text-sm font-medium text-n-slate-12">
                  {{ agendaEvent.contactName }}
                </td>
                <td class="px-4 py-3 text-sm text-n-slate-12">
                  {{ agendaEvent.reason }}
                </td>
                <td class="px-4 py-3 text-sm text-n-slate-11">
                  {{ formatDuration(agendaEvent.durationMinutes) }}
                </td>
                <td class="px-4 py-3">
                  <span
                    class="inline-flex rounded-full border px-2 py-1 text-xs font-semibold"
                    :class="statusBadgeClass(agendaEvent.status)"
                  >
                    {{ statusLabel(agendaEvent.status) }}
                  </span>
                </td>
                <td class="px-4 py-3 text-sm text-n-slate-11">
                  {{ agendaEvent.source }}
                </td>
                <td class="px-4 py-3">
                  <div class="flex flex-wrap justify-end gap-2">
                    <Button
                      variant="ghost"
                      color="slate"
                      sm
                      icon="i-lucide-pencil"
                      :label="$t('AGENDA.ACTIONS.EDIT')"
                      @click="openEditDialog(agendaEvent)"
                    />
                    <Button
                      v-if="agendaEvent.status !== 'completed'"
                      variant="ghost"
                      color="teal"
                      sm
                      icon="i-lucide-check"
                      :label="$t('AGENDA.ACTIONS.MARK_COMPLETED')"
                      @click="markCompleted(agendaEvent)"
                    />
                    <Button
                      variant="ghost"
                      color="ruby"
                      sm
                      icon="i-lucide-trash-2"
                      :label="$t('AGENDA.ACTIONS.DELETE')"
                      @click="deleteAgendaEvent(agendaEvent)"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else-if="viewMode === 'week'" class="overflow-x-auto">
          <div class="min-w-[72rem]">
            <div class="grid grid-cols-[5rem_repeat(7,minmax(0,1fr))] border-b border-n-weak bg-n-alpha-1">
              <div class="border-r border-n-weak px-3 py-3 text-xs font-semibold uppercase tracking-[0.08em] text-n-slate-10">
                {{ $t('AGENDA.FIELDS.SCHEDULED_AT') }}
              </div>
              <div
                v-for="day in weekDays"
                :key="`header-${day.toISOString()}`"
                class="border-r border-n-weak px-3 py-3 last:border-r-0"
              >
                <p class="mb-1 text-xs font-semibold uppercase tracking-[0.08em] text-n-slate-10">
                  {{ formatLocalizedDate(day, 'EEE') }}
                </p>
                <p
                  class="mb-0 text-lg font-semibold"
                  :class="isSameDay(day, todayStart) ? 'text-n-brand' : 'text-n-slate-12'"
                >
                  {{ formatLocalizedDate(day, 'd') }}
                </p>
              </div>
            </div>

            <div
              v-for="hour in hourSlots"
              :key="`hour-${hour}`"
              class="grid grid-cols-[5rem_repeat(7,minmax(0,1fr))]"
            >
              <div class="border-r border-b border-n-weak px-3 py-4 text-xs font-medium text-n-slate-10">
                {{ formatHourLabel(hour) }}
              </div>
              <div
                v-for="day in weekDays"
                :key="`${day.toISOString()}-${hour}`"
                class="min-h-[5rem] border-r border-b border-n-weak p-1.5 last:border-r-0"
              >
                <button
                  v-for="agendaEvent in eventsForDayAndHour(day, hour)"
                  :key="agendaEvent.id"
                  type="button"
                  class="mb-1.5 flex w-full flex-col gap-1 rounded-xl border border-n-weak bg-n-alpha-1 p-2 text-left transition hover:border-n-brand/30 hover:bg-n-brand/5"
                  @click="openEditDialog(agendaEvent)"
                >
                  <div class="flex items-center justify-between gap-2">
                    <span class="text-[11px] font-semibold text-n-brand">
                      {{ formatLocalizedDate(agendaEvent.date, 'HH:mm') }}
                    </span>
                    <span
                      class="inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold"
                      :class="statusBadgeClass(agendaEvent.status)"
                    >
                      {{ statusLabel(agendaEvent.status) }}
                    </span>
                  </div>
                  <p class="mb-0 truncate text-xs font-semibold text-n-slate-12">
                    {{ agendaEvent.contactName }}
                  </p>
                  <p class="mb-0 truncate text-[11px] text-n-slate-10">
                    {{ agendaEvent.reason }}
                  </p>
                  <p
                    v-if="agendaEvent.reminderEnabled"
                    class="mb-0 truncate text-[10px] text-n-brand"
                  >
                    {{ reminderSummary(agendaEvent) }}
                  </p>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="overflow-x-auto">
          <div class="min-w-[72rem]">
            <div class="grid grid-cols-7 border-b border-n-weak bg-n-alpha-1">
              <div
                v-for="dayLabel in weekdayHeaders"
                :key="dayLabel"
                class="border-r border-n-weak px-3 py-3 text-xs font-semibold uppercase tracking-[0.08em] text-n-slate-10 last:border-r-0"
              >
                {{ dayLabel }}
              </div>
            </div>
            <div class="grid min-h-[42rem] grid-cols-7 divide-x divide-y divide-n-weak">
              <div
                v-for="day in monthDays"
                :key="day.toISOString()"
                class="flex min-h-[10rem] flex-col gap-2 p-2"
                :class="{
                  'bg-n-alpha-1': isSameDay(day, todayStart),
                  'bg-n-alpha-black1/20': !isSameMonth(day, calendarCursor),
                }"
              >
                <div class="flex items-center justify-between">
                  <span
                    class="text-sm font-semibold"
                    :class="isSameMonth(day, calendarCursor) ? 'text-n-slate-12' : 'text-n-slate-10'"
                  >
                    {{ formatLocalizedDate(day, 'd') }}
                  </span>
                  <span
                    v-if="isSameDay(day, todayStart)"
                    class="inline-flex rounded-full bg-n-brand px-2 py-0.5 text-[10px] font-semibold text-white"
                  >
                    {{ $t('AGENDA.NAVIGATION.TODAY') }}
                  </span>
                </div>
                <div class="flex flex-1 flex-col gap-1">
                  <button
                    v-for="agendaEvent in eventsForDay(day).slice(0, 3)"
                    :key="agendaEvent.id"
                    type="button"
                    class="rounded-lg border border-n-weak bg-n-solid-1 px-2 py-1 text-left text-xs text-n-slate-12 transition hover:border-n-brand/30 hover:bg-n-brand/5"
                    @click="openEditDialog(agendaEvent)"
                  >
                    <div class="truncate">
                      <span class="font-semibold text-n-brand">
                        {{ formatLocalizedDate(agendaEvent.date, 'HH:mm') }}
                      </span>
                      {{ agendaEvent.contactName }}
                    </div>
                    <div
                      v-if="agendaEvent.reminderEnabled"
                      class="truncate text-[10px] text-n-brand"
                    >
                      {{ reminderSummary(agendaEvent) }}
                    </div>
                  </button>
                  <div
                    v-if="eventsForDay(day).length > 3"
                    class="px-1 text-xs font-medium text-n-slate-10"
                  >
                    {{
                      $t('AGENDA.CALENDAR.MORE', {
                        count: eventsForDay(day).length - 3,
                      })
                    }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Dialog
      ref="dialogRef"
      width="lg"
      :title="editingEventId ? $t('AGENDA.EDIT') : $t('AGENDA.ADD')"
      :confirm-button-label="
        editingEventId ? $t('AGENDA.ACTIONS.SAVE') : $t('AGENDA.ACTIONS.CREATE')
      "
      :is-loading="isSubmitting"
      :disable-confirm-button="!canSubmitAgenda"
      overflow-y-auto
      @confirm="submitAgendaEvent"
      @close="resetForm"
    >
      <div class="grid gap-4 md:grid-cols-2">
        <Input
          v-model="form.scheduledAt"
          type="datetime-local"
          :label="$t('AGENDA.FIELDS.SCHEDULED_AT')"
          required
        />
        <div class="md:col-span-2 grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto]">
          <div class="flex min-w-0 flex-col gap-1">
            <label class="mb-0.5 text-heading-3 text-n-slate-12">
              {{ $t('AGENDA.FIELDS.EXISTING_CONTACT') }}
            </label>
            <ComboBox
              v-model="selectedContactId"
              :options="contactOptions"
              use-api-results
              :message="contactPickerMessage"
              :search-placeholder="$t('AGENDA.FIELDS.EXISTING_CONTACT_SEARCH')"
              :empty-state="$t('AGENDA.FIELDS.EXISTING_CONTACT_EMPTY')"
              :placeholder="$t('AGENDA.FIELDS.EXISTING_CONTACT_PLACEHOLDER')"
              @search="debouncedLoadContacts"
            />
          </div>
          <div class="flex items-end">
            <Button
              variant="faded"
              color="slate"
              sm
              icon="i-lucide-user-plus"
              :label="$t('AGENDA.ACTIONS.QUICK_ADD_CONTACT')"
              @click="openQuickCreateContactDialog"
            />
          </div>
        </div>
        <Input
          v-model="form.contactName"
          :label="$t('AGENDA.FIELDS.CONTACT')"
          required
          class="md:col-span-2"
        />
        <Input
          v-model="form.reason"
          :label="$t('AGENDA.FIELDS.REASON')"
          required
          class="md:col-span-2"
        />
        <Input
          v-model="form.durationMinutes"
          type="number"
          min="1"
          :label="$t('AGENDA.FIELDS.DURATION')"
          required
        />
        <div class="md:col-span-2 rounded-xl border border-n-weak bg-n-alpha-1 p-4">
          <label class="flex items-start gap-3">
            <input
              v-model="form.reminderEnabled"
              type="checkbox"
              class="mt-1 size-4 rounded border-n-weak text-n-brand focus:ring-n-brand"
            />
            <span class="flex flex-col gap-1">
              <span class="text-sm font-medium text-n-slate-12">
                {{ $t('AGENDA.REMINDERS.ENABLE') }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{ $t('AGENDA.REMINDERS.DESCRIPTION') }}
              </span>
            </span>
          </label>

          <div
            v-if="form.reminderEnabled"
            class="mt-4 grid gap-4 md:grid-cols-2"
          >
            <Input
              v-model="form.reminderValue"
              type="number"
              min="1"
              max="365"
              :label="$t('AGENDA.REMINDERS.VALUE')"
              required
            />
            <div class="flex min-w-0 flex-col gap-1">
              <label class="mb-0.5 text-heading-3 text-n-slate-12">
                {{ $t('AGENDA.REMINDERS.UNIT') }}
              </label>
              <select
                v-model="form.reminderUnit"
                class="h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak transition-all duration-200 hover:outline-n-slate-6 focus:outline-n-brand"
              >
                <option value="hours">
                  {{ $t('AGENDA.REMINDERS.UNITS.HOURS') }}
                </option>
                <option value="days">
                  {{ $t('AGENDA.REMINDERS.UNITS.DAYS') }}
                </option>
              </select>
            </div>
            <div class="flex min-w-0 flex-col gap-1">
              <label class="mb-0.5 text-heading-3 text-n-slate-12">
                {{ $t('AGENDA.REMINDERS.ACTION_TYPE') }}
              </label>
              <select
                v-model="form.reminderActionType"
                class="h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak transition-all duration-200 hover:outline-n-slate-6 focus:outline-n-brand"
              >
                <option value="message">
                  {{ $t('AGENDA.REMINDERS.ACTIONS.MESSAGE') }}
                </option>
                <option value="template">
                  {{ $t('AGENDA.REMINDERS.ACTIONS.TEMPLATE') }}
                </option>
              </select>
            </div>
            <div v-if="form.reminderActionType === 'template'" class="flex min-w-0 flex-col gap-1">
              <label class="mb-0.5 text-heading-3 text-n-slate-12">
                {{ $t('AGENDA.REMINDERS.TEMPLATE') }}
              </label>
              <select
                v-model="form.reminderTemplateKey"
                class="h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak transition-all duration-200 hover:outline-n-slate-6 focus:outline-n-brand"
              >
                <option value="">
                  {{ $t('AGENDA.REMINDERS.TEMPLATE_PLACEHOLDER') }}
                </option>
                <option
                  v-for="option in reminderTemplateOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>
            <Input
              v-else
              v-model="form.reminderMessage"
              :label="$t('AGENDA.REMINDERS.CUSTOM_MESSAGE')"
              :placeholder="$t('AGENDA.REMINDERS.CUSTOM_MESSAGE_PLACEHOLDER')"
              class="md:col-span-2"
            />
          </div>
        </div>
        <div class="flex min-w-0 flex-col gap-1">
          <label class="mb-0.5 text-heading-3 text-n-slate-12">
            {{ $t('AGENDA.FIELDS.STATUS') }}
          </label>
          <select
            v-model="form.status"
            class="h-10 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak transition-all duration-200 hover:outline-n-slate-6 focus:outline-n-brand"
          >
            <option
              v-for="option in statusOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>
        <Input
          v-model="form.source"
          :label="$t('AGENDA.FIELDS.SOURCE')"
          :placeholder="sourcePlaceholder"
          class="md:col-span-2"
          required
        />
      </div>
    </Dialog>

    <CreateNewContactDialog
      ref="quickCreateContactDialogRef"
      @create="createQuickContact"
    />
  </div>
</template>
